import { firebaseProjectId } from '../firebase';
import {
  getBundleComponents,
  getBundleExtraCosts,
  getBundles,
  getMixedProductComponents,
  getMixedProductInputs,
} from './bundleArchitectureService';
import {
  getAllCategories,
  getAllComponentPriceHistory,
  getAllComponentSuppliers,
  getAllProductComponents,
  getAllProductSuppliers,
  getDashboardSummary,
  getPriceHistory,
  getProductVariants,
  getProducts,
  getSupplierContacts,
  getSuppliers,
  getUsers,
} from './firestoreService';
import { getAllExportDatasetKeys } from './settingsExportService';

export const VACANZA_OS_VERSION = '0.1.0';

const LAST_BACKUP_STORAGE_KEY = 'vacanza.lastBackupDate';

const fetchers = {
  suppliers: getSuppliers,
  supplierContacts: getSupplierContacts,
  products: getProducts,
  categories: getAllCategories,
  users: getUsers,
  productVariants: getProductVariants,
  priceHistory: getPriceHistory,
  productSuppliers: getAllProductSuppliers,
  productComponents: getAllProductComponents,
  componentSuppliers: getAllComponentSuppliers,
  componentPriceHistory: getAllComponentPriceHistory,
  mixedProductInputs: () => getMixedProductInputs({ includeDeleted: true }),
  mixedProductComponents: () => getMixedProductComponents({ includeDeleted: true }),
  bundles: () => getBundles({ includeDeleted: true }),
  bundleComponents: () => getBundleComponents({ includeDeleted: true }),
  bundleExtraCosts: () => getBundleExtraCosts({ includeDeleted: true }),
};

const uniqueDatasetKeys = (keys) =>
  Array.from(
    new Set(
      (Array.isArray(keys) ? keys : [])
        .filter((key) => Object.prototype.hasOwnProperty.call(fetchers, key))
    )
  );

export const getSettingsExportData = async (selectedKeys) => {
  const keys = uniqueDatasetKeys(selectedKeys);
  const entries = await Promise.all(
    keys.map(async (key) => [key, await fetchers[key]()])
  );

  return Object.fromEntries(entries);
};

export const getFullBackupData = async () =>
  getSettingsExportData(getAllExportDatasetKeys());

const canUseLocalStorage = () =>
  typeof window !== 'undefined' && Boolean(window.localStorage);

export const getLastBackupDate = () => {
  if (!canUseLocalStorage()) {
    return '';
  }

  return window.localStorage.getItem(LAST_BACKUP_STORAGE_KEY) || '';
};

export const recordLastBackupDate = (date = new Date().toISOString()) => {
  if (canUseLocalStorage()) {
    window.localStorage.setItem(LAST_BACKUP_STORAGE_KEY, date);
  }

  return date;
};

export const getSystemInformation = async () => {
  const [summary, users] = await Promise.all([
    getDashboardSummary(),
    getUsers(),
  ]);

  return {
    version: VACANZA_OS_VERSION,
    firebaseProjectId,
    environment: process.env.NODE_ENV || 'development',
    userCount: Array.isArray(users) ? users.length : 0,
    supplierCount: summary?.suppliers || 0,
    productCount: summary?.products || 0,
    categoryCount: summary?.categories || 0,
    lastBackupDate: getLastBackupDate(),
  };
};
